import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SITE } from "@/config/site";

// Lightweight, non-custodial Solana wallet connection (Phantom / Solflare).
// We only ever read the public address and SOL balance. We NEVER ask for
// seed phrases, private keys, or any transfer "to verify" the wallet.

const WalletContext = createContext(null);

export function detectWallets() {
  if (typeof window === "undefined") return [];
  const found = [];
  const seen = new Set();
  const push = (key, name, provider, installUrl) => {
    if (provider && !seen.has(key)) {
      seen.add(key);
      found.push({ key, name, provider, installUrl });
    }
  };
  if (window.phantom?.solana?.isPhantom) push("phantom", "Phantom", window.phantom.solana, "https://phantom.com");
  else if (window.solana?.isPhantom) push("phantom", "Phantom", window.solana, "https://phantom.com");
  if (window.solflare?.isSolflare) push("solflare", "Solflare", window.solflare, "https://solflare.com");
  return found;
}

export const WALLET_CATALOG = [
  { key: "phantom", name: "Phantom", installUrl: "https://phantom.com" },
  { key: "solflare", name: "Solflare", installUrl: "https://solflare.com" },
];

async function fetchSolBalance(address) {
  const res = await fetch(SITE.rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getBalance", params: [address] }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message);
  return json.result.value / 1e9;
}

export function WalletProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [providerName, setProviderName] = useState(null);
  const [solBalance, setSolBalance] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [balanceError, setBalanceError] = useState(false);

  const loadBalance = useCallback(async (addr) => {
    try {
      setBalanceError(false);
      setSolBalance(await fetchSolBalance(addr));
    } catch {
      setSolBalance(null);
      setBalanceError(true);
    }
  }, []);

  const connect = useCallback(async (provider, name) => {
    setConnecting(true);
    try {
      const resp = await provider.connect();
      const addr = resp.publicKey?.toString?.() ?? provider.publicKey?.toString?.();
      if (!addr) throw new Error("No public key returned");
      setAddress(addr);
      setProviderName(name);
      localStorage.setItem("mbga-wallet", name);
      loadBalance(addr);
      return addr;
    } finally {
      setConnecting(false);
    }
  }, [loadBalance]);

  const disconnect = useCallback(async () => {
    try {
      const w = detectWallets().find((x) => x.name === providerName);
      await w?.provider?.disconnect?.();
    } catch { /* ignore */ }
    setAddress(null);
    setProviderName(null);
    setSolBalance(null);
    localStorage.removeItem("mbga-wallet");
  }, [providerName]);

  // Silent reconnect for wallets that already trusted this site.
  useEffect(() => {
    const wanted = localStorage.getItem("mbga-wallet");
    if (!wanted) return;
    const w = detectWallets().find((x) => x.name === wanted);
    if (!w) return;
    w.provider.connect({ onlyIfTrusted: true }).then((resp) => {
      const addr = resp.publicKey?.toString?.() ?? w.provider.publicKey?.toString?.();
      if (addr) {
        setAddress(addr);
        setProviderName(w.name);
        loadBalance(addr);
      }
    }).catch(() => {});
  }, [loadBalance]);

  const value = useMemo(() => ({
    connected: !!address,
    address,
    providerName,
    solBalance,
    balanceError,
    connecting,
    connect,
    disconnect,
    detected: detectWallets(),
  }), [address, providerName, solBalance, balanceError, connecting, connect, disconnect]);

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export const useWallet = () => useContext(WalletContext);
